import { useState } from 'react';
import { useQuery } from 'react-query';
/** Components */
import { Container } from 'Components/Container';
import { DefaultTable } from 'Components/DefaultTable';
import { ModalConfirmDelete } from 'Components/ModalConfirmDelete';
import { PageLoaderList } from 'Components/PageLoader/List';
import { tableHeader } from './table-header.js';
/** Services */
import { deleteRopa, getRopasShortPaged } from 'services/report-ropa-service.js';
/** State Managers */
import { useAuthTenant } from 'store/auth';
import { useSelector } from 'react-redux';

/** Third Lib Modal */
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';
import { NoData } from 'Components/NoData/index.jsx';
import { Pagination, Stack } from '@mui/material';
import { PageHelper } from 'Components/PageHelper';

const ReportsRopa = () => {
  const selectedTenant = useSelector(useAuthTenant);
  const SwalReact = withReactContent(Swal);
  const [open, setOpen] = useState(false);
  const [reFetch, setRefetch] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [page, setPage] = useState(1);
  const {
    isLoading,
    error,
    data: pagedRopa,
  } = useQuery(['getRopas', reFetch, page], async () => {
    if (selectedTenant.slug === undefined) return [];
    const result = await getRopasShortPaged(selectedTenant.slug, page, 15);
    if (result?.data.data.length > 0) {
      return result?.data;
    }
    return {
      data: [],
      page,
      totalPages: 0,
    };
  });

  const onDelete = item => {
    setItemToDelete(item);
    setOpen(true);
  };

  const onClose = () => {
    setItemToDelete(null);
    setOpen(false);
  };

  const onConfirm = async () => {
    try {
      const result = await deleteRopa(itemToDelete?._id);
      if (result.status === 204) {
        onClose();
        setRefetch(!reFetch);
      } else {
        throw new Error('Ocorreu um erro ao tentar remover o Relatório!');
      }
    } catch (error) {
      SwalReact.fire({
        title: 'Erro!',
        text: error.response.data.message ?? error.message,
        icon: 'error',
      });
    }
  };

  return (
    <Container 
      title="Relatórios" 
      breadcrumb="Relatórios / Ropa" 
      showButtonAdd 
      linkPage="/reports/ropa"
      data-tour="page-title"
      addButtonDataTour="add-button"
    >
      {isLoading && <PageLoaderList></PageLoaderList>}
      {error && <div>Ocorreu um erro ...</div>}
      {!isLoading && !error && pagedRopa.data?.length === 0 && (
        <NoData description={'Não há Relatórios Ropa para exibir'} />
      )}
      {!isLoading && !error && pagedRopa.data?.length > 0 && (
        <div className="container">
          <div data-tour="table">
          <DefaultTable
            rows={tableHeader}
            cells={pagedRopa.data}
            onClickDelete={onDelete}
            hideEdit
            link="reports/ropa"
          />
          </div>
          <Stack sx={{ justifyContent: 'center', flexDirection: 'row', marginTop: '16px' }} data-tour="pagination">
            <Pagination
              count={pagedRopa.totalPages}
              onChange={(_, selectedPage) => {
                setPage(selectedPage);
              }}
              color="primary"
              page={page}
              showFirstButton
              showLastButton
            />
          </Stack>
          <ModalConfirmDelete
            description={
              <>
                Tem certeza que deseja excluir o relatório <strong>{` ${itemToDelete?.name} `}</strong>?
              </>
            }
            open={open}
            onClose={onClose}
            onConfirm={onConfirm}
          />
        </div>
      )}
      <PageHelper steps={tourSteps} tourKey="reports-ropa" />
    </Container>
  );
};

const tourSteps = [
  {
    target: 'body',
    content: 'Bem-vindo aos Relatórios ROPA (Registro de Operações de Processamento de Dados)! Aqui você gerencia e visualiza os relatórios de conformidade com a LGPD.',
    disableBeacon: true,
    disableScrolling: true,
    placement: 'center',
  },
  {
    target: '[data-tour="page-title"]',
    content: 'Clique no título para voltar à página anterior.',
    disableScrolling: true,
  },
  {
    target: '[data-tour="add-button"]',
    content: 'Use o botão "Adicionar" para criar um novo relatório ROPA, documentando as operações de processamento de dados da sua organização.',
    disableScrolling: true,
  },
  {
    target: '[data-tour="table"]',
    content: 'Esta tabela lista todos os relatórios ROPA criados. Cada relatório documenta atividades de processamento, finalidades, categorias de dados e medidas de segurança.',
    disableScrolling: false,
  },
  {
    target: '[data-tour="pagination"]',
    content: 'Use a paginação para navegar entre as páginas quando houver muitos relatórios.',
    disableScrolling: false,
  },
  {
    target: 'body',
    content: 'Tour concluído! Mantenha seus relatórios ROPA atualizados para garantir conformidade contínua com a LGPD.',
    disableScrolling: true,
    placement: 'center',
  },
];

export default ReportsRopa;
